import { type SyntheticEvent, useCallback } from "react";

import { type IAnalyticsExtraInfoValue } from "./payload";
import { type ComponentName } from "./registry/componentNames";
import { type ComponentType } from "./registry/componentTypes";
import { type EventName } from "./registry/eventNames";
import { useAnalytics } from "./useAnalytics";

export interface IUseAnalyticsClickHandlerOptions {
  /**
   * The Sawmill v3 `eventName` to emit on click. When `undefined`, the returned
   * handler is a no-op unless overridden per-invocation (see `IAnalyticsClickOverrides`).
   * Lets consumers opt in per-instance without conditional hook calls.
   */
  eventName: EventName | undefined;
  /**
   * Optional `componentType` — hardcode `"button"`, `"link"`, `"modal"`, etc.
   * from the calling component so the hook stays agnostic.
   */
  componentType?: ComponentType;
  /**
   * Optional `componentName` (e.g. `"approve_button"`, `"bill_number_link"`).
   */
  componentName?: ComponentName;
  /**
   * Optional `extraInfo` merged into the emitted event (e.g. `{ bill_id }`).
   */
  extraInfo?: Record<string, IAnalyticsExtraInfoValue>;
  /**
   * The job this click concerns — the event's top-level `jobId`, which is what
   * the warehouse attributes on.
   *
   * Distinct from `extraInfo.selected_job_id`, which the enricher fills with the
   * job picker's current selection. On an "All Jobs" list those are different
   * jobs, so a consumer that knows the real one should say so here.
   *
   * Omitted entirely when absent rather than sent as `null`: `buildEvent`
   * resolves an unset `jobId` from the surrounding context, and an explicit
   * `null` would clobber a value a page-level provider had seeded.
   */
  jobId?: number;
}

/**
 * Per-invocation overrides. Use when the same hook binding is shared across
 * many click targets (e.g. rows in a list) and each click needs its own
 * `eventName`/`componentName`.
 */
export interface IAnalyticsClickOverrides {
  /**
   * The Sawmill v3 `eventName` to emit on click. When present, wins over the
   * hook-seeded `eventName`; when absent, the seeded value applies. If the
   * resolved (overridden ⇢ seeded) value is still `undefined`, the call is a
   * no-op.
   */
  eventName?: EventName;
  /**
   * The `componentName` for this click (e.g. `"approve_button"`,
   * `"bill_number_link"`). When present, wins over the hook-seeded value.
   */
  componentName?: ComponentName;
  /**
   * `extraInfo` for this click (e.g. `{ bill_id }`). Merged with the
   * hook-seeded `extraInfo` — per-click keys win on conflict.
   */
  extraInfo?: Record<string, IAnalyticsExtraInfoValue>;
  /**
   * The `jobId` for this click. Wins over the hook-seeded value — the case a
   * grid cell needs, where one hook binding serves rows belonging to different
   * jobs.
   */
  jobId?: number;
}

/**
 * True when the argument is a plain overrides object rather than something
 * else the caller might have leaked in (e.g. a `MouseEvent` when the handler
 * is bound directly to `onClick={fire}`). Overrides must be a plain object with
 * only the known override keys — SyntheticEvent, DOM Event, and other class
 * instances are all rejected.
 */
/**
 * The returned handler, as an overload pair rather than a single
 * `(arg?: unknown)` signature.
 *
 * Both documented usages have to typecheck: called with overrides
 * (`fire({ eventName })`) and bound straight to a handler prop
 * (`onClick={fire}`, where React supplies a `SyntheticEvent` the runtime guard
 * discards). A single widened parameter would allow both, but it would also
 * accept anything else — so a typo'd override key would compile and then be
 * silently dropped by `isOverrides` at runtime. These two signatures permit
 * exactly the two intended shapes and reject the rest at the call site.
 */
export interface IAnalyticsClickHandler {
  (overrides?: IAnalyticsClickOverrides): void;
  (event: SyntheticEvent): void;
}

function isOverrides(arg: unknown): arg is IAnalyticsClickOverrides {
  if (typeof arg !== "object" || arg === null) return false;
  if (Object.getPrototypeOf(arg) !== Object.prototype) return false;
  const allowed = new Set(["eventName", "componentName", "extraInfo", "jobId"]);
  for (const key of Object.keys(arg)) {
    if (!allowed.has(key)) return false;
  }
  return true;
}

/**
 * Returns a memoized click handler that emits a Sawmill v3 `click` event when
 * invoked. Two usage modes:
 *
 * 1. **Static**: seed everything at hook-call time, call with no args:
 *    ```
 *    const fire = useAnalyticsClickHandler({ eventName: "approve_bill", ... });
 *    <BdsButton onClick={() => fire()} />
 *    ```
 *
 * 2. **Dynamic** (list-mapping): seed shared config, supply per-click config at
 *    the call site — perfect when a single component renders many click targets
 *    that each track a different `eventName`/`componentName`:
 *    ```
 *    const fire = useAnalyticsClickHandler({ componentType: "link", eventName: undefined });
 *    rows.map(row => <a onClick={() => fire({ eventName: row.event, componentName: row.name })} />)
 *    ```
 *
 * Binding directly (`onClick={fire}`) is safe: the React `MouseEvent` React
 * passes as the first arg is rejected by the runtime guard, so the seeded
 * config wins.
 *
 * If the resolved `eventName` (seeded ⇢ overridden) is still `undefined`, the
 * call is a no-op.
 */
export function useAnalyticsClickHandler(
  options: IUseAnalyticsClickHandlerOptions,
): IAnalyticsClickHandler {
  const { trackAnalytics } = useAnalytics();
  const { eventName, componentType, componentName, extraInfo, jobId } = options;

  return useCallback(
    (maybeOverrides?: unknown) => {
      const overrides = isOverrides(maybeOverrides) ? maybeOverrides : {};
      const resolvedEventName = overrides.eventName ?? eventName;
      if (!resolvedEventName) {
        return;
      }
      const resolvedComponentName = overrides.componentName ?? componentName;
      const hasExtraInfo = Boolean(overrides.extraInfo || extraInfo);
      const resolvedJobId = overrides.jobId ?? jobId;
      trackAnalytics({
        eventType: "click",
        eventName: resolvedEventName,
        componentType: componentType ?? null,
        componentName: resolvedComponentName ?? null,
        // Same omit-when-absent rule as extraInfo below, for the same reason:
        // an explicit undefined is disallowed under exactOptionalPropertyTypes,
        // and an explicit null would override the context-resolved jobId.
        ...(resolvedJobId !== undefined && { jobId: resolvedJobId }),
        // Omit the property entirely when neither side supplied it — the target
        // type disallows explicit `undefined` under exactOptionalPropertyTypes.
        ...(hasExtraInfo && {
          extraInfo: { ...extraInfo, ...overrides.extraInfo },
        }),
      });
    },
    [componentName, componentType, eventName, extraInfo, jobId, trackAnalytics],
  );
}
